import React, { useState, useEffect, useCallback } from 'react';
import { X, CalendarClock, Loader2, Check } from 'lucide-react';
import api from '../../services/api';

// Staff-initiated reschedule: one operation, not cancel-then-book. Picks a new
// slot for the case's upcoming appointment; the backend replaces it atomically
// and sends the single client notification. Slot list is the SAME engine the
// propose flow uses (/appointments/my-slots).
export default function RescheduleAppointmentModal({ isOpen, onClose, caseId, appointment, onRescheduled }) {
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [picked, setPicked] = useState(null); // {start,end,label}
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const loadSlots = useCallback(() => {
    setSlotsLoading(true);
    api.get('/appointments/my-slots', { params: { days: 14 } })
      .then((r) => setSlots(r.data?.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, []);

  useEffect(() => { if (isOpen) loadSlots(); }, [isOpen, loadSlots]);
  useEffect(() => { if (!isOpen) { setPicked(null); setResult(null); setError(''); } }, [isOpen]);

  if (!isOpen || !appointment) return null;

  const submit = async () => {
    if (!picked) { setError('Pick a new time first.'); return; }
    setSubmitting(true); setError('');
    try {
      const { data } = await api.post(`/client-portal/staff/cases/${caseId}/appointments/reschedule`, {
        start_time: picked.start,
        end_time: picked.end,
      });
      setResult(data);
      if (onRescheduled) onRescheduled(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not reschedule the appointment.');
    } finally { setSubmitting(false); }
  };

  const byDate = slots.reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {});
  const fmtDate = (d) => new Date(d + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-virgo-navy-800 border border-virgo-teal-700/50 rounded-2xl shadow-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-virgo-teal-700/40">
          <div className="flex items-center gap-2 text-white font-semibold"><CalendarClock size={18} /> Reschedule Appointment</div>
          <button onClick={onClose} className="text-virgo-teal-400 hover:text-white"><X size={18} /></button>
        </div>

        {result ? (
          <div className="p-6 space-y-3 text-sm text-virgo-teal-100">
            <div className="flex items-center gap-2 text-emerald-300">
              <Check size={18} /> Moved to {result.local_date} at {result.local_time} {result.timezone_label}.
            </div>
            <div className="text-virgo-teal-400">
              The previous time is cancelled and {appointment.contact_name || 'the client'} was sent a single reschedule notice — no separate cancellation email.
            </div>
            <button onClick={onClose} className="mt-2 px-4 py-2 rounded-lg bg-virgo-teal-600 hover:bg-virgo-teal-500 text-white">Done</button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="bg-virgo-navy-900 border border-virgo-teal-700/30 rounded-lg p-3 text-sm">
              <div className="text-xs text-virgo-teal-400 uppercase mb-1">Current appointment</div>
              <div className="text-virgo-teal-100">
                {appointment.contact_name} · {appointment.local_date} at {appointment.local_time} {appointment.timezone_label}
                {appointment.meeting_type ? ` · ${appointment.meeting_type.replace('_', '-')}` : ''}
                {appointment.status === 'pending_approval' ? ' · pending approval' : ''}
              </div>
            </div>

            <div>
              <label className="block text-xs text-virgo-teal-400 mb-2">New time</label>
              {slotsLoading ? (
                <div className="flex items-center gap-2 text-virgo-teal-400 text-sm"><Loader2 size={14} className="animate-spin" /> Loading open slots…</div>
              ) : slots.length === 0 ? (
                <div className="text-sm text-virgo-teal-400">No open slots in the next 14 days.</div>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                  {Object.entries(byDate).map(([d, daySlots]) => (
                    <div key={d}>
                      <div className="text-xs text-virgo-teal-500 mb-1">{fmtDate(d)}</div>
                      <div className="flex flex-wrap gap-2">
                        {daySlots.map((s) => {
                          const selected = picked?.start === s.start_time;
                          return (
                            <button key={s.start_time}
                              onClick={() => setPicked({ start: s.start_time, end: s.end_time, label: `${fmtDate(s.date)} ${s.local_time}` })}
                              className={`text-xs px-3 py-1.5 rounded-lg border ${selected
                                ? 'bg-virgo-teal-600 text-white border-virgo-teal-500'
                                : 'bg-virgo-navy-900 text-virgo-teal-300 border-virgo-teal-700/50 hover:bg-virgo-navy-700'}`}>
                              {s.local_time}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && <div className="text-sm text-red-400">{String(error)}</div>}

            <button onClick={submit} disabled={submitting || !picked}
              className="w-full px-4 py-2 rounded-lg bg-virgo-teal-600 hover:bg-virgo-teal-500 disabled:opacity-50 text-white text-sm font-medium flex items-center justify-center gap-2">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />}
              {picked ? `Move to ${picked.label}` : 'Pick a new time'}
            </button>
            <div className="text-xs text-virgo-teal-500">
              One notice goes to the client with the old and new time — the cancel and rebook happen together.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
